import { useState, useEffect } from "react";
import type { Incident, IncidentSeverity } from "../../types/incidents";
import { adminFetch } from "../../lib/api";
import { apiFetch } from "../../lib/config";

const SEVERITIES: IncidentSeverity[] = ["minor", "major", "critical"];

interface SR { id: string; label: string; }

function SeverityBadge({ v }: { v: IncidentSeverity }) {
  return <span className={`ac-badge ac-badge--${v}`}>{v}</span>;
}

function StatusBadge({ v }: { v: string }) {
  return <span className={`ac-badge ac-badge--${v}`}>{v}</span>;
}

function fmtDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function IncidentManagementPage() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [serviceRegions, setServiceRegions] = useState<SR[]>([]);
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "resolved">("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Incident | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatePanelId, setUpdatePanelId] = useState<string | null>(null);
  const [updateMsg, setUpdateMsg] = useState("");

  // create form state
  const [cTitle, setCTitle] = useState("");
  const [cSeverity, setCSeverity] = useState<IncidentSeverity>("minor");
  const [cMessage, setCMessage] = useState("");
  const [cSRs, setCSRs] = useState<string[]>([]);
  const [cError, setCError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // edit form state
  const [eTitle, setETitle] = useState("");
  const [eSeverity, setESeverity] = useState<IncidentSeverity>("minor");
  const [eError, setEError] = useState("");
  const [eSubmitting, setESubmitting] = useState(false);

  async function load() {
    const r = await apiFetch("/api/v1/incidents/");
    if (r.ok) {
      const d = await r.json();
      setIncidents(d.results ?? d);
    }
  }

  async function loadSRs() {
    // Use the admin service-regions endpoint (lightweight) instead of the full
    // status grid which carries probe results and is much heavier.
    const r = await adminFetch("/api/v1/admin/service-regions/");
    if (!r.ok) return;
    const d: SR[] = await r.json();
    setServiceRegions(d);
  }

  useEffect(() => { Promise.all([load(), loadSRs()]); }, []);

  function toggleSR(id: string) {
    setCSRs(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  }

  async function createIncident(e: React.FormEvent) {
    e.preventDefault();
    if (!cTitle.trim()) { setCError("Title is required."); return; }
    setSubmitting(true); setCError("");
    try {
      const r = await adminFetch("/api/v1/admin/incidents/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: cTitle, severity: cSeverity, service_region_ids: cSRs }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(JSON.stringify(e)); }
      const created = await r.json();
      if (cMessage.trim()) {
        await adminFetch(`/api/v1/admin/incidents/${created.id}/updates/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: cMessage }),
        });
      }
      setShowCreate(false); setCTitle(""); setCMessage(""); setCSRs([]);
      await load();
    } catch (err) {
      setCError(`Failed: ${err}`);
    } finally { setSubmitting(false); }
  }

  async function postUpdate(incidentId: string) {
    if (!updateMsg.trim()) return;
    const r = await adminFetch(`/api/v1/admin/incidents/${incidentId}/updates/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: updateMsg }),
    });
    if (r.ok) {
      const created = await r.json();
      const msg = updateMsg;
      setIncidents(p => p.map(i =>
        i.id === incidentId
          ? { ...i, updates: [...(i.updates ?? []), { id: created.id, message: msg, created_at: new Date().toISOString(), created_by: "you" }] }
          : i
      ));
    }
    setUpdatePanelId(null); setUpdateMsg("");
  }

  async function resolve(id: string) {
    if (!confirm("Mark this incident as resolved?")) return;
    const r = await adminFetch(`/api/v1/admin/incidents/${id}/resolve/`, { method: "POST" });
    if (r.ok) {
      setIncidents(p => p.map(i =>
        i.id === id ? { ...i, status: "resolved", resolved_at: new Date().toISOString() } : i
      ));
    }
  }

  async function removeIncident(id: string, title: string) {
    if (!confirm(`Permanently delete incident "${title}"? This cannot be undone.`)) return;
    const r = await adminFetch(`/api/v1/admin/incidents/${id}/`, { method: "DELETE" });
    if (r.ok) {
      setIncidents(p => p.filter(i => i.id !== id));
    }
  }

  function openEdit(inc: Incident) {
    setETitle(inc.title);
    setESeverity(inc.severity);
    setEError("");
    setEditTarget(inc);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editTarget) return;
    if (!eTitle.trim()) { setEError("Title is required."); return; }
    setESubmitting(true); setEError("");
    try {
      const r = await adminFetch(`/api/v1/admin/incidents/${editTarget.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: eTitle.trim(), severity: eSeverity }),
      });
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.detail || `HTTP ${r.status}`); }
      setIncidents(p => p.map(i => i.id === editTarget.id ? { ...i, title: eTitle.trim(), severity: eSeverity } : i));
      setEditTarget(null);
    } catch (err) {
      setEError(err instanceof Error ? err.message : "Failed to save changes.");
    } finally { setESubmitting(false); }
  }

  const filtered = incidents.filter(i => filterStatus === "all" || i.status === filterStatus);
  const activeCount = incidents.filter(i => i.status === "active").length;
  const criticalCount = incidents.filter(i => i.severity === "critical" && i.status === "active").length;

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Incidents</h1>
          <p className="ac-page-subtitle">Track, update, and resolve platform incidents</p>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--primary" onClick={() => setShowCreate(true)}>
            + Create Incident
          </button>
        </div>
      </div>

      <div className="ac-summary-strip">
        <div className="ac-summary-card">
          <div className="ac-summary-card__value">{incidents.length}</div>
          <div className="ac-summary-card__label">Total</div>
        </div>
        <div className={`ac-summary-card${activeCount > 0 ? " ac-summary-card--warn" : " ac-summary-card--ok"}`}>
          <div className="ac-summary-card__value">{activeCount}</div>
          <div className="ac-summary-card__label">Active</div>
        </div>
        <div className={`ac-summary-card${criticalCount > 0 ? " ac-summary-card--danger" : ""}`}>
          <div className="ac-summary-card__value">{criticalCount}</div>
          <div className="ac-summary-card__label">Critical</div>
        </div>
        <div className="ac-summary-card ac-summary-card--ok">
          <div className="ac-summary-card__value">{incidents.filter(i => i.status === "resolved").length}</div>
          <div className="ac-summary-card__label">Resolved</div>
        </div>
      </div>

      <div className="ac-filter-bar">
        <select className="aptean-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value as typeof filterStatus)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="resolved">Resolved</option>
        </select>
        <span className="ac-filter-bar__sep" />
        <span style={{ fontSize: "0.8125rem", color: "var(--aptean-text-secondary)" }}>
          {filtered.length} incident{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="ac-table-wrap">
        <table className="ac-table">
          <thead>
            <tr>
              <th style={{ width: "36%" }}>Title</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Affected Services</th>
              <th>Created</th>
              <th>Resolved</th>
              <th className="ac-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7}>
                <div className="ac-empty">
                  <div className="ac-empty__icon">&#9888;</div>
                  <div className="ac-empty__title">No incidents</div>
                  <div className="ac-empty__body">No incidents match the current filter.</div>
                </div>
              </td></tr>
            ) : filtered.map(inc => (
              <>
                <tr key={inc.id}>
                  <td>
                    <button type="button" style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}
                      onClick={() => setExpandedId(expandedId === inc.id ? null : inc.id)}>
                      <div className="ac-row-title">{inc.title}</div>
                      {inc.updates?.length > 0 && (
                        <div className="ac-row-sub">{inc.updates.length} update{inc.updates.length !== 1 ? "s" : ""}</div>
                      )}
                    </button>
                  </td>
                  <td><SeverityBadge v={inc.severity} /></td>
                  <td><StatusBadge v={inc.status} /></td>
                  <td className="ac-td--muted">
                    {inc.affected_services?.length > 0
                      ? inc.affected_services.map(s => `${s.service_name} (${s.region_name})`).join(", ")
                      : "—"}
                  </td>
                  <td className="ac-td--muted">{fmtDate(inc.created_at)}</td>
                  <td className="ac-td--muted">{fmtDate(inc.resolved_at)}</td>
                  <td className="ac-td--actions ac-td--right">
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                      <button type="button" className="aptean-btn aptean-btn--ghost"
                        style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                        onClick={() => openEdit(inc)}>
                        Edit
                      </button>
                      {inc.status === "active" && (
                        <>
                          <button type="button" className="aptean-btn aptean-btn--secondary"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => { setUpdatePanelId(updatePanelId === inc.id ? null : inc.id); setUpdateMsg(""); }}>
                            Add Update
                          </button>
                          <button type="button" className="aptean-btn aptean-btn--ghost"
                            style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                            onClick={() => resolve(inc.id)}>
                            Resolve
                          </button>
                        </>
                      )}
                      <button type="button" className="aptean-btn aptean-btn--danger"
                        style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }}
                        onClick={() => removeIncident(inc.id, inc.title)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {updatePanelId === inc.id && (
                  <tr key={`${inc.id}-update`} className="ac-detail-row">
                    <td colSpan={7}>
                      <div className="ac-inline-update">
                        <textarea className="aptean-input" rows={2} value={updateMsg}
                          onChange={e => setUpdateMsg(e.target.value)}
                          placeholder="Describe the update..." />
                        <button type="button" className="aptean-btn aptean-btn--primary"
                          onClick={() => postUpdate(inc.id)} disabled={!updateMsg.trim()}>
                          Post
                        </button>
                        <button type="button" className="aptean-btn aptean-btn--ghost"
                          onClick={() => setUpdatePanelId(null)}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
                {expandedId === inc.id && inc.updates?.length > 0 && (
                  <tr key={`${inc.id}-detail`} className="ac-detail-row">
                    <td colSpan={7}>
                      <div className="ac-detail-panel">
                        <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--aptean-text-secondary)", marginBottom: "0.25rem" }}>Update history</div>
                        <div className="ac-timeline">
                          {[...inc.updates].reverse().map(u => (
                            <div key={u.id} className="ac-timeline-entry">
                              <span className="ac-timeline-entry__time">{fmtDate(u.created_at)}</span>
                              <span className="ac-timeline-entry__dot" />
                              <div className="ac-timeline-entry__body">
                                <span>{u.message}</span>
                                <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-muted)", marginLeft: "0.5rem" }}>— {u.created_by}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setEditTarget(null)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Modify Incident</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setEditTarget(null)} aria-label="Close">&#10005;</button>
            </div>
            <form onSubmit={saveEdit}>
              <div className="ac-dialog__body">
                {eError && <div className="ac-alert ac-alert--error">{eError}</div>}
                <div className="ac-field">
                  <label htmlFor="e-title">Title *</label>
                  <input id="e-title" type="text" className="aptean-input" value={eTitle} onChange={e => setETitle(e.target.value)} required />
                </div>
                <div className="ac-field">
                  <label htmlFor="e-severity">Severity</label>
                  <select id="e-severity" className="aptean-input" value={eSeverity} onChange={e => setESeverity(e.target.value as IncidentSeverity)}>
                    {SEVERITIES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setEditTarget(null)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={eSubmitting}>
                  {eSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Create Incident</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowCreate(false)} aria-label="Close">&#10005;</button>
            </div>
            <form onSubmit={createIncident}>
              <div className="ac-dialog__body">
                {cError && <div className="ac-alert ac-alert--error">{cError}</div>}
                <div className="ac-field">
                  <label htmlFor="c-title">Title *</label>
                  <input id="c-title" type="text" className="aptean-input" value={cTitle} onChange={e => setCTitle(e.target.value)} placeholder="Brief description of the incident" required />
                </div>
                <div className="ac-field">
                  <label htmlFor="c-severity">Severity</label>
                  <select id="c-severity" className="aptean-input" value={cSeverity} onChange={e => setCSeverity(e.target.value as IncidentSeverity)}>
                    {SEVERITIES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                {serviceRegions.length > 0 && (
                  <div className="ac-field">
                    <label>Affected service regions</label>
                    <div className="ac-checkbox-list">
                      {serviceRegions.map(sr => (
                        <label key={sr.id} className="ac-checkbox-item">
                          <input type="checkbox" checked={cSRs.includes(sr.id)} onChange={() => toggleSR(sr.id)} />
                          {sr.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="ac-field">
                  <label htmlFor="c-msg">Initial update message</label>
                  <textarea id="c-msg" className="aptean-input" rows={3} value={cMessage} onChange={e => setCMessage(e.target.value)} placeholder="Optional — initial status update for the incident" />
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={submitting}>{submitting ? "Creating..." : "Create Incident"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
